import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';

/**
 * Password reset page.
 *
 * Supported arrival formats:
 * 1) Recommended (anti-prefetch): /reset-password?token_hash=...&type=recovery
 *    → user clicks "Continue" then we call verifyOtp (POST). Email scanners that
 *      only GET the page cannot burn the token.
 * 2) Legacy hash session: /reset-password#access_token=...&type=recovery
 *    → Supabase detectSessionInUrl / onAuthStateChange
 * 3) Already signed-in session (local testing while logged in)
 */
const ResetPassword = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [sessionError, setSessionError] = useState('');
  const [pendingTokenHash, setPendingTokenHash] = useState(null);
  const sessionReadyRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const { data: authListener } = supabase?.auth?.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        sessionReadyRef.current = true;
        if (!cancelled) {
          setPendingTokenHash(null);
          setSessionReady(true);
          setSessionError('');
        }
      }
    });

    const markReady = () => {
      sessionReadyRef.current = true;
      if (!cancelled) {
        setPendingTokenHash(null);
        setSessionReady(true);
        setSessionError('');
      }
    };

    const init = async () => {
      const hash = window.location?.hash || '';
      const hashParams = new URLSearchParams(hash.replace(/^#/, ''));
      const searchParams = new URLSearchParams(window.location?.search || '');

      // Failed legacy verify redirect: #error=access_denied&error_code=otp_expired
      const hashError = hashParams.get('error_code') || hashParams.get('error');
      if (hashError) {
        const desc = hashParams.get('error_description')?.replace(/\+/g, ' ');
        if (!cancelled) {
          setSessionError(
            desc ||
              'This reset link is invalid or was already used. Request a new password reset and open the newest email once.',
          );
        }
        return;
      }

      const tokenHash = searchParams.get('token_hash');
      const type = searchParams.get('type');
      if (tokenHash && type === 'recovery') {
        // Do NOT verify automatically — wait for an explicit user click.
        if (!cancelled) setPendingTokenHash(tokenHash);
        return;
      }

      const { data: sessionData } = await supabase?.auth?.getSession();
      if (cancelled) return;
      if (sessionData?.session) {
        markReady();
        return;
      }

      // Legacy: #access_token processed by detectSessionInUrl
      setTimeout(() => {
        if (!cancelled && !sessionReadyRef.current && !pendingTokenHash) {
          setSessionError(
            'Invalid or expired reset link. Request a new password reset and open the newest email once.',
          );
        }
      }, 4000);
    };

    init();

    return () => {
      cancelled = true;
      authListener?.subscription?.unsubscribe?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirmRecovery = async () => {
    if (!pendingTokenHash) return;
    setVerifying(true);
    setSessionError('');
    try {
      const { error: otpError } = await supabase.auth.verifyOtp({
        token_hash: pendingTokenHash,
        type: 'recovery',
      });
      if (otpError) {
        setSessionError(
          otpError.message ||
            'This reset link is invalid or was already used. Please request a new one.',
        );
        setPendingTokenHash(null);
        return;
      }
      sessionReadyRef.current = true;
      setPendingTokenHash(null);
      setSessionReady(true);
      // Clean sensitive query params from the address bar
      window.history.replaceState({}, document.title, '/reset-password');
    } catch (err) {
      setSessionError(err?.message || 'Could not verify reset link. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!form?.newPassword?.trim()) {
      setError('Please enter a new password.');
      return;
    }
    if (form?.newPassword?.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (form?.newPassword !== form?.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase?.auth?.updateUser({
        password: form?.newPassword,
      });

      if (updateError) {
        setError(updateError?.message || 'Failed to update password. Please try again.');
      } else {
        setSuccess(true);
        await supabase?.auth?.signOut();
        setTimeout(() => navigate('/super-admin', { replace: true }), 3000);
      }
    } catch (err) {
      setError(err?.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
            <Icon name="KeyRound" size={28} className="text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Set New Password</h1>
          <p className="text-sm text-muted-foreground mt-1 text-center">
            Enter your new password below to complete the reset.
          </p>
        </div>

        <div className="bg-card border border-border rounded-2xl shadow-xl p-6">
          {success ? (
            <div className="flex flex-col items-center gap-3 py-4">
              <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center">
                <Icon name="CheckCircle" size={24} className="text-green-500" />
              </div>
              <p className="text-sm font-semibold text-foreground text-center">
                Password updated successfully!
              </p>
              <p className="text-xs text-muted-foreground text-center">
                Redirecting you to the login page…
              </p>
            </div>
          ) : sessionError ? (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="w-12 h-12 bg-destructive/10 rounded-xl flex items-center justify-center">
                <Icon name="AlertCircle" size={24} className="text-destructive" />
              </div>
              <p className="text-sm font-medium text-foreground text-center">{sessionError}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/super-admin', { replace: true })}
                className="mt-1"
              >
                Back to Login
              </Button>
            </div>
          ) : pendingTokenHash ? (
            <div className="flex flex-col items-center gap-4 py-4">
              <p className="text-sm text-muted-foreground text-center">
                Click below to verify this reset link and choose a new password.
              </p>
              <Button
                onClick={handleConfirmRecovery}
                disabled={verifying}
                className="w-full flex items-center justify-center gap-2"
              >
                {verifying ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Verifying…
                  </>
                ) : (
                  <>
                    <Icon name="KeyRound" size={15} />
                    Continue to reset password
                  </>
                )}
              </Button>
            </div>
          ) : !sessionReady ? (
            <div className="flex flex-col items-center gap-3 py-6">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-muted-foreground">Verifying reset link…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  New Password <span className="text-destructive">*</span>
                </label>
                <Input
                  type="password"
                  value={form?.newPassword}
                  onChange={(e) => setForm((prev) => ({ ...prev, newPassword: e?.target?.value }))}
                  placeholder="••••••••"
                  autoFocus
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Confirm Password <span className="text-destructive">*</span>
                </label>
                <Input
                  type="password"
                  value={form?.confirmPassword}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, confirmPassword: e?.target?.value }))
                  }
                  placeholder="••••••••"
                  required
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 px-3 py-2 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <Icon name="AlertCircle" size={15} className="text-destructive flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Icon name="Save" size={15} />
                    Save New Password
                  </>
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
